import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../api/admin";

// Trial length is fixed in the Go licence server and in
// services/trial_service.TRIAL_DAYS. If that ever changes, update here too —
// /api/admin/licences exposes the activation date, not an expiry.
const TRIAL_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

// The countdown starts at first activation in the plugin, never at issuance
// (services/trial_service.trial_view). Counting from created_at showed
// "6 days left" for keys nobody had ever installed — badly misleading now
// that a licence is handed over at email capture and many are never opened.
function trialStatus(item) {
  const activatedAt = item.trial_activated_at;
  if (!activatedAt) return { activated: false };
  const activated = new Date(activatedAt);
  // Belt and braces with the backend's isoformat: an unparseable date would
  // otherwise reach toISOString() below and throw, blanking the whole page.
  if (Number.isNaN(activated.getTime())) return { activated: false };
  const ends = new Date(activated.getTime() + TRIAL_DAYS * DAY_MS);
  const msLeft = ends - new Date();
  return {
    activated: true,
    ends,
    daysLeft: Math.max(0, Math.ceil(msLeft / DAY_MS)),
    expired: msLeft <= 0,
  };
}

function MachineRow({ idx, machine, licenceKey, onDeactivate }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!window.confirm(`Deactivate Machine ${idx + 1} on ${licenceKey}?`)) return;
    setBusy(true);
    try { await onDeactivate(licenceKey, machine.fingerprint); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 bg-[#0a0a0c] rounded px-4 py-2">
      <div className="min-w-0">
        <p className="text-sm text-white">Machine {idx + 1}</p>
        <p className="text-xs text-[#A1A1AA] mt-0.5">
          Activated {new Date(machine.activated_at).toLocaleDateString()}
          {" · "}
          Last seen {new Date(machine.last_validated_at).toLocaleDateString()}
        </p>
      </div>
      <button
        onClick={handle}
        disabled={busy}
        className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40"
      >
        {busy ? "..." : "Deactivate"}
      </button>
    </div>
  );
}

function LicenceRow({ item, onRevoke, onDeactivateMachine, secondary }) {
  const [open, setOpen] = useState(false);
  const k = item.licence;
  const machines = item.machines || [];

  return (
    <div className="feature-card rounded-lg">
      <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 text-left flex items-center gap-2"
        >
          <span className="text-[#A1A1AA] text-xs w-3">{open ? "▾" : "▸"}</span>
          <div>
            <code className="text-sm text-white font-mono break-all">{k.licence_key}</code>
            <p className="text-xs text-[#A1A1AA] mt-1 break-words">
              {k.customer_email || <span className="italic text-[#666]">no email</span>}
              {" · "}
              {machines.length}/{k.max_machines} machines
              {secondary && <> · {secondary}</>}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-3 md:pl-3">
          <span className={`text-xs ${k.revoked ? "text-red-400" : "text-green-400"}`}>
            {k.revoked ? "Revoked" : "Active"}
          </span>
          {!k.revoked && (
            <button onClick={() => onRevoke(k.licence_key)} className="text-xs text-red-400 hover:text-red-300">
              Revoke
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-white/5 px-6 py-3 space-y-2">
          {machines.length === 0 && (
            <p className="text-xs text-[#666]">No machines activated.</p>
          )}
          {machines.map((m, idx) => (
            <MachineRow
              key={m.fingerprint}
              idx={idx}
              machine={m}
              licenceKey={k.licence_key}
              onDeactivate={onDeactivateMachine}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function LicencesAdminPage() {
  const [licences, setLicences] = useState([]);
  const [search, setSearch] = useState("");

  const load = () => adminApi.getLicences().then((resp) => setLicences(resp.data));
  useEffect(() => { load(); }, []);

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke licence ${key}?`)) return;
    await adminApi.revokeLicence(key);
    setLicences((prev) =>
      prev.map((l) => l.licence.licence_key === key
        ? { ...l, licence: { ...l.licence, revoked: true } }
        : l)
    );
  };

  const handleDeactivateMachine = async (licenceKey, fingerprint) => {
    await adminApi.deactivateMachine(licenceKey, fingerprint);
    setLicences((prev) =>
      prev.map((l) => l.licence.licence_key === licenceKey
        ? { ...l, machines: (l.machines || []).filter((m) => m.fingerprint !== fingerprint) }
        : l)
    );
  };

  const { paid, trials } = useMemo(() => {
    const q = search.toLowerCase();
    const matches = (item) =>
      !q ||
      item.licence.licence_key.toLowerCase().includes(q) ||
      (item.licence.customer_email || "").toLowerCase().includes(q);

    const paid = [];
    const trials = [];
    for (const item of licences) {
      if (!matches(item)) continue;
      if (item.licence.licence_type === "trial") trials.push(item);
      else paid.push(item);
    }
    return { paid, trials };
  }, [licences, search]);

  return (
    <div>
      <h1 className="font-clash text-2xl font-bold text-white mb-6">Licences</h1>
      <input
        type="text"
        placeholder="Search by key or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-md bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white mb-6 focus:border-[#FF5500] focus:outline-none"
      />

      <h2 className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] mb-3">
        Paid · {paid.length}
      </h2>
      <div className="space-y-2 mb-8">
        {paid.length === 0 && <p className="text-xs text-[#666]">No paid licences.</p>}
        {paid.map((item) => (
          <LicenceRow
            key={item.licence.licence_key}
            item={item}
            onRevoke={handleRevoke}
            onDeactivateMachine={handleDeactivateMachine}
            secondary="Lifetime"
          />
        ))}
      </div>

      <h2 className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] mb-3">
        Trials · {trials.length}
      </h2>
      <div className="space-y-2">
        {trials.length === 0 && <p className="text-xs text-[#666]">No trial licences.</p>}
        {trials.map((item) => {
          const status = trialStatus(item);
          const remaining = !status.activated
            ? "Not activated · countdown not started"
            : status.expired
              ? "Expired"
              : `${status.daysLeft} day${status.daysLeft === 1 ? "" : "s"} left · ends ${status.ends.toISOString().slice(0, 10)}`;
          return (
            <LicenceRow
              key={item.licence.licence_key}
              item={item}
              onRevoke={handleRevoke}
              onDeactivateMachine={handleDeactivateMachine}
              secondary={remaining}
            />
          );
        })}
      </div>
    </div>
  );
}
